/**
 * The organization's current health issues, shared by the Health monitor panel and the camera
 * replacement warning in camera settings; the health log is read by request.
 *
 * The issues are built from the notifications stream alone: the cloud replays every issue when
 * the stream opens and pushes each change as it happens. An issue is replaced by the next
 * message about the same problem and dropped by the info that ends it. While the stream is
 * down, the issues read as stale: one may have ended unseen.
 */

import { type Atom, atom, type Getter, type Setter } from 'jotai';
import { atomWithReset, RESET, selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { isCameraReplacementWarning } from '@/lib/healthMessages';
import {
    type CameraReplacementWarning, type FeedHealthMessage, HealthFeed, type HealthFrame,
    HealthFrameAction,
} from '@/types/healthMessages';
import { type StreamInput, StreamInputKind } from '@/types/notifications';

type Records = ReadonlyMap<string, FeedHealthMessage>;

const issuesAtom = atomWithReset<Records>(new Map());
const staleAtom = atom(false);

/** The current issues, in no particular order; the panel groups them for its own reading. */
export const healthIssuesAtom = atom((get): FeedHealthMessage[] => [
    ...get(issuesAtom).values(),
]);

/** Whether the issues may be out of date: the stream is down past its grace period. */
export const healthIssuesStaleAtom = atom((get): boolean => get(staleAtom));

/**
 * The current replacement warning of each camera, by camera: shown in the camera's settings
 * until the original hardware is gone again.
 */
export const replacementWarningsAtom = atom((
    get,
): ReadonlyMap<string, CameraReplacementWarning> => {
    const warnings = new Map<string, CameraReplacementWarning>();
    for (const record of get(issuesAtom).values()) {
        if (isCameraReplacementWarning(record.message)) {
            warnings.set(record.message.details.deviceId, record.message);
        }
    }
    return warnings;
});

/** The warning of one camera. A change to another camera's issues leaves it as it is. */
export const replacementWarningAtom = atomFamily(
    (deviceId: string): Atom<CameraReplacementWarning | null> => selectAtom(
        replacementWarningsAtom,
        (warnings): CameraReplacementWarning | null => warnings.get(deviceId) ?? null,
    ));

/** The warning of no camera, for a reader that has no camera yet. */
export const noReplacementWarningAtom: Atom<CameraReplacementWarning | null> = atom(null);

/** The record a put frame carries; null when the frame carries none. */
function getRecord(frame: HealthFrame): FeedHealthMessage | null {
    const { key, message } = frame;
    return key !== '' && message ? { key, message } : null;
}

function putIssue(get: Getter, set: Setter, record: FeedHealthMessage): void {
    const currentRecords = get(issuesAtom);
    // Every reconnect replays every issue; one that changes nothing must wake nobody.
    if (currentRecords.get(record.key)?.message.id === record.message.id) {
        return;
    }
    set(issuesAtom, new Map(currentRecords).set(record.key, record));
}

function deleteIssue(get: Getter, set: Setter, key: string): void {
    const nextMap = new Map(get(issuesAtom));
    if (nextMap.delete(key)) {
        set(issuesAtom, nextMap);
    }
}

/** What the notifications stream means for the health issues. */
export const healthMessagesStreamAtom = atom(null, (get, set, input: StreamInput): void => {
    switch (input.kind) {
        case StreamInputKind.Down:
            set(staleAtom, true);
            return;
        case StreamInputKind.Reopened:
            // The reopened stream replays the issues, and those ended in the gap are not in the
            // replay; whatever was held would shadow those endings.
            set(issuesAtom, RESET);
            set(staleAtom, false);
            return;
        case StreamInputKind.Frame:
            break;
    }
    const { frame } = input;
    if (frame.type !== 'health' || frame.feed !== HealthFeed.Issues) {
        return;
    }
    switch (frame.action) {
        case HealthFrameAction.Delete:
            deleteIssue(get, set, frame.key);
            return;
        case HealthFrameAction.Put: {
            const record = getRecord(frame);
            if (record) {
                putIssue(get, set, record);
            }
            return;
        }
    }
});
